"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IndexBasedGridSelection = exports.SelectionBehavior = void 0;
var SelectionBehavior;
(function (SelectionBehavior) {
    SelectionBehavior[SelectionBehavior["SelectCell"] = 0] = "SelectCell";
    SelectionBehavior[SelectionBehavior["SelectColumn"] = 1] = "SelectColumn";
    SelectionBehavior[SelectionBehavior["SelectRow"] = 2] = "SelectRow";
    SelectionBehavior[SelectionBehavior["None"] = 3] = "None";
})(SelectionBehavior = exports.SelectionBehavior || (exports.SelectionBehavior = {}));
var SelectionRangeType;
(function (SelectionRangeType) {
    SelectionRangeType[SelectionRangeType["Row"] = 0] = "Row";
    SelectionRangeType[SelectionRangeType["Col"] = 1] = "Col";
    SelectionRangeType[SelectionRangeType["Cell"] = 2] = "Cell";
})(SelectionRangeType || (SelectionRangeType = {}));
/**
 * Tracks the state of selected cells/rows/cols. Provides methods to modify the selection and methods
 * to see if a cell/row/col is selected.
 *
 * @see `RowKeyedGridSelection` if you're only interested in tracking selection at the granularity of rows.
 *
 * @example
 * const MyComponent: React.FC = () => {
 *   // could also use a ref here; useState syntax is slightly cleaner
 *   const [selectionModel] = React.useState(new IndexBasedGridSelection(rowCount, colCount));
 *
 *   const handleSelectionEvent: React.useCallback<CellSelectionHandler>(e => {
 *     // here is where you might signal something outside the component if something interesting is happening
 *
 *     return selectionModel.handleSelectionEvent(e); // IndexBasedGridSelection provides a "standard" handler which allows consistent UX across grids
 *   }, [selectionModel]);
 *
 *   return <DataGrid selectionModel={selectionModel} onSelectionEvent={handleSelectionEvent} {...otherPropsHere} />;
 * }
 */
var IndexBasedGridSelection = /** @class */ (function () {
    function IndexBasedGridSelection(rows, cols) {
        var _this = this;
        /**
         * The anchor point is used for ranged selection as the starting point. It is set from the row, col,
         * or cell selections. If it is a row or col selection, it is converted to a Cell by converting the row to
         * {rowIdx: row, cell: 0} and vice-versa.  It is initialized as {0, 0}
         */
        this.anchorPoint = { rowIdx: 0, colIdx: 0 };
        /**
         * The last selection range type is tracked because for range selections the last selection is removed.
         */
        this.lastSelectionRangeType = null;
        // Configuration options
        this.selectionBehavior = SelectionBehavior.SelectCell;
        this.selectColumnOnHeaderClick = true;
        this.handleSelectionEvent = function (e) {
            var cell = e.cell;
            var colIdx = cell.colIdx, rowIdx = cell.rowIdx;
            if (_this.selectionBehavior === SelectionBehavior.None || (colIdx >= 0 && rowIdx === -1 && !_this.selectColumnOnHeaderClick)) {
                return false;
            }
            var rowSelection = colIdx === -1 || (colIdx >= 0 && rowIdx >= 0 && _this.selectionBehavior === SelectionBehavior.SelectRow);
            var colSelection = rowIdx === -1 || (colIdx >= 0 && rowIdx >= 0 && _this.selectionBehavior === SelectionBehavior.SelectColumn);
            var mouseButtons = e.buttons;
            var dragSelect = mouseButtons === 1 && e.type === 'mouseenter';
            var key = e.key;
            var arrow = !!key && key.indexOf('Arrow') === 0;
            var space = !!key && key === ' ';
            var home = !!key && key === 'Home';
            var end = !!key && key === 'End';
            var esc = !!key && key === 'Escape';
            var ctrl = e.ctrlKey;
            var shift = e.shiftKey || dragSelect;
            var ctrlA = (ctrl && !!key && (key === 'a' || key === 'A'));
            if (arrow && ctrl && !shift) {
                return false;
            }
            else if (!!mouseButtons && mouseButtons !== 1) {
                return false;
            }
            else if (esc) {
                _this.clearSelection();
            }
            else if (ctrlA) {
                _this.selectAll();
            }
            else if (rowSelection && colSelection) {
                if (ctrl && !shift && _this.isSelected({ colIdx: -1, rowIdx: -1 })) {
                    _this.clearSelection();
                }
                else {
                    _this.selectAll();
                }
            }
            else if (shift) {
                if (!ctrl) {
                    _this.clearSelection();
                }
                rowSelection ? _this.rangeRowSelectFromAnchor(rowIdx) : colSelection ? _this.rangeColSelectFromAnchor(colIdx) : _this.rangeCellSelectFromAnchor(cell);
            }
            else {
                _this.setAnchorPoint(cell);
                if (ctrl && !home && !end) {
                    rowSelection ? _this.toggleRowSelect(rowIdx) : colSelection ? _this.toggleColumnSelect(colIdx) : _this.toggleCellSelect(cell);
                }
                else {
                    if (space) {
                        rowSelection ? _this.inclusiveRowSelect(rowIdx) : colSelection ? _this.inclusiveColSelect(colIdx) : _this.inclusiveCellSelect(cell);
                    }
                    else {
                        rowSelection ? _this.exclusiveRowSelect(rowIdx) : colSelection ? _this.exclusiveColSelect(colIdx) : _this.exclusiveCellSelect(cell);
                    }
                }
            }
            return true;
        };
        this.selection = {
            rowRanges: [],
            colRanges: [],
            cellRanges: [],
            gridDimensions: { rows: rows, cols: cols }
        };
    }
    /** Clears selection, but does not reset the anchor point */
    IndexBasedGridSelection.prototype.clearSelection = function () {
        this.selection.rowRanges = [];
        this.selection.colRanges = [];
        this.selection.cellRanges = [];
        this.lastSelectionRangeType = null;
    };
    IndexBasedGridSelection.prototype.resetAnchorPoint = function () {
        this.anchorPoint = { rowIdx: 0, colIdx: 0 };
    };
    /**
     * This initializes the selection data, sets the last selection range type to null, and the anchor point
     * to {0, 0}.
     */
    IndexBasedGridSelection.prototype.initializeSelection = function (selectionData) {
        this.selection = selectionData;
        this.lastSelectionRangeType = null;
        this.resetAnchorPoint();
    };
    /**
     * This does not add a selection, but merely sets the anchor point for ranged selections.
     */
    IndexBasedGridSelection.prototype.setAnchorPoint = function (anchorPoint) {
        this.anchorPoint = anchorPoint;
    };
    IndexBasedGridSelection.prototype.getSelection = function () {
        return this.selection;
    };
    IndexBasedGridSelection.prototype.isSelected = function (cell) {
        if (cell.colIdx === -1 && cell.rowIdx === -1) {
            return IndexBasedGridSelection.areAllRowsSelected(this.selection) || IndexBasedGridSelection.areAllColsSelected(this.selection);
        }
        else if (cell.colIdx === -1) {
            return IndexBasedGridSelection.isRowSelected(cell.rowIdx, this.selection);
        }
        else if (cell.rowIdx === -1) {
            return IndexBasedGridSelection.isColSelected(cell.colIdx, this.selection);
        }
        else {
            return IndexBasedGridSelection.isCellSelected(cell, this.selection);
        }
    };
    /*
     * Static Query Methods
     */
    IndexBasedGridSelection.isCellSelected = function (cell, selectedCells) {
        return IndexBasedGridSelection.isRowSelected(cell.rowIdx, selectedCells) ||
            IndexBasedGridSelection.isColSelected(cell.colIdx, selectedCells) ||
            IndexBasedGridSelection.isWithinCellRanges(cell, selectedCells);
    };
    IndexBasedGridSelection.isRowSelected = function (row, selectedCells) {
        return IndexBasedGridSelection.isInNumRanges(row, selectedCells.rowRanges) ||
            IndexBasedGridSelection.areAllCellsSelected(SelectionRangeType.Row, row, selectedCells, selectedCells.gridDimensions.cols);
    };
    IndexBasedGridSelection.isColSelected = function (col, selectedCells) {
        return IndexBasedGridSelection.isInNumRanges(col, selectedCells.colRanges) ||
            IndexBasedGridSelection.areAllCellsSelected(SelectionRangeType.Col, col, selectedCells, selectedCells.gridDimensions.rows);
    };
    IndexBasedGridSelection.areAllCellsSelected = function (type, index, selectionData, count) {
        var _this = this;
        if (type !== SelectionRangeType.Row && type !== SelectionRangeType.Col) {
            return false;
        }
        if (count <= 0) {
            return false;
        }
        var cellSelection = {};
        var updateCellSelection = function (begin, end) {
            end = Math.min(count, end);
            for (var idx = begin; idx <= end; idx++) {
                if (idx >= 0 && idx < count && !cellSelection[idx]) {
                    cellSelection[idx] = true;
                }
            }
        };
        var numRanges = (type === SelectionRangeType.Row) ? selectionData.colRanges : selectionData.rowRanges;
        numRanges.forEach(function (numRange) {
            numRange = _this.normalizeNumRange(numRange);
            updateCellSelection(numRange.begin, numRange.end);
        });
        var checkNumRange;
        var updateNumRange;
        selectionData.cellRanges.forEach(function (cellRange) {
            cellRange = _this.normalizeCellRange(cellRange);
            if (type === SelectionRangeType.Row) {
                checkNumRange = { begin: cellRange.begin.rowIdx, end: cellRange.end.rowIdx };
                updateNumRange = { begin: cellRange.begin.colIdx, end: cellRange.end.colIdx };
            }
            else {
                checkNumRange = { begin: cellRange.begin.colIdx, end: cellRange.end.colIdx };
                updateNumRange = { begin: cellRange.begin.rowIdx, end: cellRange.end.rowIdx };
            }
            if (IndexBasedGridSelection.isBetweenNumRange(index, checkNumRange.begin, checkNumRange.end)) {
                updateCellSelection(updateNumRange.begin, updateNumRange.end);
            }
        });
        return Object.keys(cellSelection).length === count;
    };
    IndexBasedGridSelection.areAllRowsSelected = function (selectionData) {
        for (var rowIdx = 0; rowIdx < selectionData.gridDimensions.rows; rowIdx++) {
            if (!IndexBasedGridSelection.isRowSelected(rowIdx, selectionData)) {
                return false;
            }
        }
        return true;
    };
    IndexBasedGridSelection.areAllColsSelected = function (selectionData) {
        for (var colIdx = 0; colIdx < selectionData.gridDimensions.cols; colIdx++) {
            if (!IndexBasedGridSelection.isColSelected(colIdx, selectionData)) {
                return false;
            }
        }
        return true;
    };
    IndexBasedGridSelection.isInNumRanges = function (num, numRanges) {
        for (var i = 0; i < numRanges.length; i++) {
            if (IndexBasedGridSelection.isInNumRange(num, numRanges[i])) {
                return true;
            }
        }
        return false;
    };
    IndexBasedGridSelection.isInNumRange = function (num, numRange) {
        numRange = IndexBasedGridSelection.normalizeNumRange(numRange);
        return IndexBasedGridSelection.isBetweenNumRange(num, numRange.begin, numRange.end);
    };
    IndexBasedGridSelection.isWithinCellRanges = function (cell, selectedCells) {
        for (var i = 0; i < selectedCells.cellRanges.length; i++) {
            if (IndexBasedGridSelection.isWithinCellRange(cell, selectedCells.cellRanges[i])) {
                return true;
            }
        }
        return false;
    };
    IndexBasedGridSelection.isWithinCellRange = function (cell, cellRange) {
        cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
        var withinCol = IndexBasedGridSelection.isBetweenNumRange(cell.colIdx, cellRange.begin.colIdx, cellRange.end.colIdx);
        var withinRow = IndexBasedGridSelection.isBetweenNumRange(cell.rowIdx, cellRange.begin.rowIdx, cellRange.end.rowIdx);
        return withinCol && withinRow;
    };
    IndexBasedGridSelection.isBetweenNumRange = function (num, begin, end) {
        return (num >= begin && num <= end) || (num >= end && num <= begin);
    };
    IndexBasedGridSelection.normalizeNumRange = function (numRange) {
        return numRange.begin <= numRange.end ? numRange : { begin: numRange.end, end: numRange.begin };
    };
    IndexBasedGridSelection.normalizeCellRange = function (cellRange) {
        var beginCell = cellRange.begin;
        var endCell = cellRange.end;
        if (beginCell.rowIdx <= endCell.rowIdx && beginCell.colIdx <= endCell.colIdx) {
            return cellRange;
        }
        else if (endCell.rowIdx <= beginCell.rowIdx && endCell.colIdx <= beginCell.colIdx) {
            return {
                begin: endCell,
                end: beginCell
            };
        }
        else if (endCell.rowIdx <= beginCell.rowIdx && beginCell.colIdx <= endCell.colIdx) {
            return {
                begin: {
                    rowIdx: endCell.rowIdx,
                    colIdx: beginCell.colIdx
                },
                end: {
                    rowIdx: beginCell.rowIdx,
                    colIdx: endCell.colIdx
                }
            };
        }
        return {
            begin: {
                rowIdx: beginCell.rowIdx,
                colIdx: endCell.colIdx
            },
            end: {
                rowIdx: endCell.rowIdx,
                colIdx: beginCell.colIdx
            }
        };
    };
    /*
     * Select actions
     */
    IndexBasedGridSelection.prototype.deselectRow = function (row) {
        this.removeRow(row);
        this.removeCellsFromRow(row);
        this.removeSelectedCols(row);
    };
    IndexBasedGridSelection.prototype.toggleRowSelect = function (row) {
        if (IndexBasedGridSelection.isRowSelected(row, this.selection)) {
            this.deselectRow(row);
        }
        else {
            this.inclusiveRowSelect(row);
        }
    };
    IndexBasedGridSelection.prototype.deselectColumn = function (col) {
        this.removeColumn(col);
        this.removeCellsFromColumn(col);
        this.removeSelectedRows(col);
    };
    IndexBasedGridSelection.prototype.toggleColumnSelect = function (col) {
        if (IndexBasedGridSelection.isColSelected(col, this.selection)) {
            this.deselectColumn(col);
        }
        else {
            this.inclusiveColSelect(col);
        }
    };
    IndexBasedGridSelection.prototype.deselectCell = function (cell) {
        if (IndexBasedGridSelection.isRowSelected(cell.rowIdx, this.selection)) {
            this.removeRow(cell.rowIdx);
            this.addCellRangesForRemovedRow(cell);
        }
        if (IndexBasedGridSelection.isColSelected(cell.colIdx, this.selection)) {
            this.removeColumn(cell.colIdx);
            this.addCellRangesForRemovedColumn(cell);
        }
        this.removeCell(cell);
    };
    IndexBasedGridSelection.prototype.toggleCellSelect = function (cell) {
        if (IndexBasedGridSelection.isCellSelected(cell, this.selection)) {
            this.deselectCell(cell);
        }
        else {
            this.inclusiveCellSelect(cell);
        }
    };
    /**
     * This happens typically when ctrl + pointerPressing.  It adds to the selected cells and the sets it as the
     * last selected cell.
     */
    IndexBasedGridSelection.prototype.inclusiveCellSelect = function (cell) {
        this.selection.cellRanges.push({ begin: cell, end: cell });
        this.lastSelectionRangeType = SelectionRangeType.Cell;
        this.setAnchorPointFromCell(cell);
    };
    /**
     * This happens typically when pointerPressing or navigating with arrow keys.  It clears the selected cells,
     * then adds the cell to selected cells, and then sets it as the last selected cell.
     */
    IndexBasedGridSelection.prototype.exclusiveCellSelect = function (cell) {
        this.clearSelection();
        this.inclusiveCellSelect(cell);
    };
    /**
     * This happens typically when ctrl + pointerPressing on the row header.  It adds to the selected rows and the sets it as the
     * last selected row.
     */
    IndexBasedGridSelection.prototype.inclusiveRowSelect = function (row) {
        this.selection.rowRanges.push({ begin: row, end: row });
        this.lastSelectionRangeType = SelectionRangeType.Row;
        this.setAnchorPointFromRow(row);
    };
    /**
     * Clears out any current selection and replace it with the new specified selection.
     */
    IndexBasedGridSelection.prototype.exclusiveRowsSelect = function (rows) {
        this.clearSelection();
        for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
            var row = rows_1[_i];
            this.inclusiveRowSelect(row);
        }
    };
    /**
     * This happens typically when pointerPressing on the row header.  It clears the selected cells, then adds the row to selected
     * rows, and then sets it as the last selected row.
     */
    IndexBasedGridSelection.prototype.exclusiveRowSelect = function (row) {
        this.clearSelection();
        this.inclusiveRowSelect(row);
    };
    /**
     * This happens typically when ctrl + pointerPressing on the col header.  It adds to the selected cols and the sets it as the
     * last selected col.
     */
    IndexBasedGridSelection.prototype.inclusiveColSelect = function (col) {
        this.selection.colRanges.push({ begin: col, end: col });
        this.lastSelectionRangeType = SelectionRangeType.Col;
        this.setAnchorPointFromCol(col);
    };
    /**
     * This happens typically when pointerPressing on the col header.  It clears the selected cells, then adds the col to selected
     * cols, and then sets it as the last selected col.
     */
    IndexBasedGridSelection.prototype.exclusiveColSelect = function (col) {
        this.clearSelection();
        this.inclusiveColSelect(col);
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given row.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    IndexBasedGridSelection.prototype.rangeRowSelectFromAnchor = function (row) {
        this.removeLastSelectedRangeIfNeeded();
        var rowRange = IndexBasedGridSelection.normalizeNumRange({ begin: this.anchorPoint.rowIdx, end: row });
        this.selection.rowRanges.push(rowRange);
        this.lastSelectionRangeType = SelectionRangeType.Row;
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given coll.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    IndexBasedGridSelection.prototype.rangeColSelectFromAnchor = function (col) {
        this.removeLastSelectedRangeIfNeeded();
        var colRange = IndexBasedGridSelection.normalizeNumRange({ begin: this.anchorPoint.colIdx, end: col });
        this.selection.colRanges.push(colRange);
        this.lastSelectionRangeType = SelectionRangeType.Col;
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given cell.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    IndexBasedGridSelection.prototype.rangeCellSelectFromAnchor = function (cell) {
        this.removeLastSelectedRangeIfNeeded();
        var cellRange = IndexBasedGridSelection.normalizeCellRange({ begin: this.anchorPoint, end: cell });
        this.selection.cellRanges.push(cellRange);
        this.lastSelectionRangeType = SelectionRangeType.Cell;
    };
    /**
     * This method clears the selected cells, then adds the rowRange and colRange to the selected rows and cols.
     */
    IndexBasedGridSelection.prototype.selectRowAndColRange = function (rowRange, colRange) {
        this.clearSelection();
        this.selection.rowRanges.push(IndexBasedGridSelection.normalizeNumRange(rowRange));
        this.selection.colRanges.push(IndexBasedGridSelection.normalizeNumRange(colRange));
    };
    /**
     * This method selects the entire grid by selecting all rows.
     */
    IndexBasedGridSelection.prototype.selectAll = function () {
        this.clearSelection();
        this.selection.rowRanges.push({ begin: 0, end: Infinity });
    };
    /**
     * This pops the last selection type from the selection.
     */
    IndexBasedGridSelection.prototype.removeLastSelectedRangeIfNeeded = function () {
        switch (this.lastSelectionRangeType) {
            case SelectionRangeType.Cell:
                this.selection.cellRanges.pop();
                break;
            case SelectionRangeType.Col:
                this.selection.colRanges.pop();
                break;
            case SelectionRangeType.Row:
                this.selection.rowRanges.pop();
                break;
            default:
                return;
        }
    };
    IndexBasedGridSelection.prototype.setAnchorPointFromCol = function (col) {
        this.anchorPoint = { rowIdx: 0, colIdx: col };
    };
    IndexBasedGridSelection.prototype.setAnchorPointFromRow = function (row) {
        this.anchorPoint = { rowIdx: row, colIdx: 0 };
    };
    IndexBasedGridSelection.prototype.setAnchorPointFromCell = function (cell) {
        this.anchorPoint = cell;
    };
    IndexBasedGridSelection.prototype.removeRow = function (row) {
        var trimmedRowRanges = [];
        this.selection.rowRanges.forEach(function (rowRange) {
            if (IndexBasedGridSelection.isInNumRange(row, rowRange)) {
                if (row - rowRange.begin > 0) {
                    trimmedRowRanges.push({ begin: rowRange.begin, end: row - 1 });
                }
                if (rowRange.end - row > 0 || rowRange.end === Infinity) {
                    trimmedRowRanges.push({ begin: row + 1, end: rowRange.end });
                }
            }
            else {
                trimmedRowRanges.push(rowRange);
            }
        });
        this.selection.rowRanges = trimmedRowRanges;
    };
    IndexBasedGridSelection.prototype.removeColumn = function (column) {
        var trimmedColRanges = [];
        this.selection.colRanges.forEach(function (colRange) {
            if (IndexBasedGridSelection.isInNumRange(column, colRange)) {
                if (column - colRange.begin > 0) {
                    trimmedColRanges.push({ begin: colRange.begin, end: column - 1 });
                }
                if (colRange.end - column > 0 || colRange.end === Infinity) {
                    trimmedColRanges.push({ begin: column + 1, end: colRange.end });
                }
            }
            else {
                trimmedColRanges.push(colRange);
            }
        });
        this.selection.colRanges = trimmedColRanges;
    };
    IndexBasedGridSelection.prototype.removeCell = function (cell) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            if (IndexBasedGridSelection.isWithinCellRange(cell, cellRange)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (cell.rowIdx - beginCell.rowIdx > 0) {
                    trimmedCellRanges.push({
                        begin: beginCell,
                        end: { rowIdx: cell.rowIdx - 1, colIdx: endCell.colIdx }
                    });
                }
                if (endCell.rowIdx - cell.rowIdx > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: cell.rowIdx + 1, colIdx: beginCell.colIdx },
                        end: endCell
                    });
                }
                if (cell.colIdx - beginCell.colIdx > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: cell.rowIdx, colIdx: beginCell.colIdx },
                        end: { rowIdx: cell.rowIdx, colIdx: cell.colIdx - 1 }
                    });
                }
                if (endCell.colIdx - cell.colIdx > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: cell.rowIdx, colIdx: cell.colIdx + 1 },
                        end: { rowIdx: cell.rowIdx, colIdx: endCell.colIdx }
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    IndexBasedGridSelection.prototype.removeCellsFromRow = function (row) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
            if (IndexBasedGridSelection.isBetweenNumRange(row, cellRange.begin.rowIdx, cellRange.end.rowIdx)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (row - beginCell.rowIdx > 0) {
                    trimmedCellRanges.push({
                        begin: beginCell,
                        end: { rowIdx: row - 1, colIdx: endCell.colIdx }
                    });
                }
                if (endCell.rowIdx - row > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: row + 1, colIdx: beginCell.colIdx },
                        end: endCell
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    IndexBasedGridSelection.prototype.removeSelectedCols = function (row) {
        var _this = this;
        this.selection.colRanges.forEach(function (colRange) {
            if (row > 0) {
                _this.selection.cellRanges.push({
                    begin: { rowIdx: 0, colIdx: colRange.begin },
                    end: { rowIdx: row - 1, colIdx: colRange.end }
                });
            }
            _this.selection.cellRanges.push({
                begin: { rowIdx: row + 1, colIdx: colRange.begin },
                end: { rowIdx: Infinity, colIdx: colRange.end }
            });
        });
        this.selection.colRanges = [];
    };
    IndexBasedGridSelection.prototype.removeCellsFromColumn = function (col) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
            if (IndexBasedGridSelection.isBetweenNumRange(col, cellRange.begin.colIdx, cellRange.end.colIdx)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (col - beginCell.colIdx > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: beginCell.rowIdx, colIdx: beginCell.colIdx },
                        end: { rowIdx: endCell.rowIdx, colIdx: col - 1 }
                    });
                }
                if (endCell.colIdx - col > 0) {
                    trimmedCellRanges.push({
                        begin: { rowIdx: beginCell.rowIdx, colIdx: col + 1 },
                        end: { rowIdx: endCell.rowIdx, colIdx: endCell.colIdx }
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    IndexBasedGridSelection.prototype.removeSelectedRows = function (col) {
        var _this = this;
        this.selection.rowRanges.forEach(function (rowRange) {
            if (col > 0) {
                _this.selection.cellRanges.push({
                    begin: { rowIdx: rowRange.begin, colIdx: 0 },
                    end: { rowIdx: rowRange.end, colIdx: col - 1 }
                });
            }
            _this.selection.cellRanges.push({
                begin: { rowIdx: rowRange.begin, colIdx: col + 1 },
                end: { rowIdx: rowRange.end, colIdx: Infinity }
            });
        });
        this.selection.rowRanges = [];
    };
    IndexBasedGridSelection.prototype.addCellRangesForRemovedRow = function (cell) {
        if (cell.colIdx > 0) {
            this.selection.cellRanges.push({
                begin: { rowIdx: cell.rowIdx, colIdx: 0 },
                end: { rowIdx: cell.rowIdx, colIdx: cell.colIdx - 1 }
            });
        }
        this.selection.cellRanges.push({
            begin: { rowIdx: cell.rowIdx, colIdx: cell.colIdx + 1 },
            end: { rowIdx: cell.rowIdx, colIdx: Infinity }
        });
    };
    IndexBasedGridSelection.prototype.addCellRangesForRemovedColumn = function (cell) {
        if (cell.rowIdx > 0) {
            this.selection.cellRanges.push({
                begin: { rowIdx: 0, colIdx: cell.colIdx },
                end: { rowIdx: cell.rowIdx - 1, colIdx: cell.colIdx }
            });
        }
        this.selection.cellRanges.push({
            begin: { rowIdx: cell.rowIdx + 1, colIdx: cell.colIdx },
            end: { rowIdx: Infinity, colIdx: cell.colIdx }
        });
    };
    return IndexBasedGridSelection;
}());
exports.IndexBasedGridSelection = IndexBasedGridSelection;
//# sourceMappingURL=IndexBasedGridSelection.js.map